import {
    ArrayShape,
    CallExpressionShape,
    IdentifierShape,
    ObjectShape,
    Plugin,
    type Record as SdkRecord,
    VariableStatementShape,
    type Database,
    type Shape,
} from '@servicenow/sdk-build-core'
import { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import {
    PLAYBOOK_TABLE,
    PLAYBOOK_API_NAME,
    LANE_TABLE,
    TIMER_ATTRIBUTES_TABLE,
    ACTIVITY_TABLE,
    ACTIVITY_API_NAME,
    TRIGGER_INSTANCE_TABLE,
    VARIANT_TABLE,
    SYS_VARIABLE_VALUE_TABLE,
    SYS_ELEMENT_MAPPING_TABLE,
    PROCESS_INPUT_TABLE,
    PARENT_RECORD,
    PARENT_RECORD_MAPPER_KEY,
    PLAYBOOK_DEFAULT_PROCESS_TYPE_DB,
    PLAYBOOK_PROCESS_TYPE_TABLE,
    getPlaybookProcessTypeSysId,
    PLAYBOOK_OUTPUT_TABLE,
    PLAYBOOK_INPUT_ATTRIBUTES,
} from '../constants/playbook-plugin-constants'
import { slugifyString } from '../../utils/slugify.js'
import { deduplicateNames } from '../../utils/unique-names.js'
import { stripHtmlTags } from '../utils/label-utils'
import { LanePlugin, laneRecordToShape, extractDecisionBranchIdMap } from './lane-plugin'
import { ActivityPlugin } from './activity-plugin'
import { LaneShape, ActivityShape } from '../utils/playbook-shapes'
import { buildChildCollectionToShape } from '../transforms/child-collection-toshape'
import { processChildCollectionToRecord, type ChildCollectionTypeConfig } from '../transforms/child-collection-torecord'
import { buildPlaybookConfigToShape, buildPlaybookRecordProperties } from '../transforms/playbook-config-transform'
import { resolveStartsAfterLanes, resolveStartsAfterBranches } from '../transforms/reference-resolver'
import { detectRedundantEdges } from '../utils/graph-utils'
import { validateOrderUniqueness } from '../utils/order-uniqueness-validator'
import { activityRecordToShape } from '../transforms/activity-config-transform'
import { processTriggersArrayToRecord, buildTriggersArrayToShape } from '../transforms/trigger-array-processing'
import type { ActivityIdentifier, PillTransformContext } from '../utils/playbook-pill-transformer'
import { isPlaybookUnsupported } from '../utils/transform-utils'
import { buildPlaybookVariableRecords, buildPlaybookVariableShapes } from '../utils/playbook-io-utils'

/**
 * Build an interleaved ArrowFunctionShape containing both Lane and Decision entries,
 * sorted by `order` field. Decisions are processed first to accumulate branchGuidMap
 * so that lanes' startRule can resolve branch references (e.g. decision_0.branches.critical).
 */
function buildInterleavedBodyToShape(
    source: SdkRecord,
    laneRecords: SdkRecord[],
    decisionRecords: SdkRecord[],
    database: Database,
    timerRecords: SdkRecord[],
    globalActivityMap: Map<string, ActivityIdentifier>
): ArrowFunctionShape {
    // Tag each record with its type and sort by order
    type TaggedEntry = { type: 'lane' | 'decision'; record: SdkRecord; order: number }
    const entries: TaggedEntry[] = []

    for (const rec of laneRecords) {
        const order = rec.get('order')?.ifString()?.getValue()
        entries.push({ type: 'lane', record: rec, order: order !== undefined ? Number(order) : 0 })
    }
    for (const rec of decisionRecords) {
        const order = rec.get('order')?.ifString()?.getValue()
        entries.push({ type: 'decision', record: rec, order: order !== undefined ? Number(order) : 0 })
    }

    entries.sort((a, b) => a.order - b.order)

    // Assign variable names with separate counters
    let laneCounter = 0
    let decisionCounter = 0
    const guidToVarName = new Map<string, string>()
    const varNames: string[] = []

    for (const entry of entries) {
        if (entry.type === 'lane') {
            const varName = `lane_${laneCounter++}`
            guidToVarName.set(entry.record.getId().getValue(), varName)
            varNames.push(varName)
        } else {
            const varName = `decision_${decisionCounter++}`
            guidToVarName.set(entry.record.getId().getValue(), varName)
            varNames.push(varName)
        }
    }

    // Register stage-level decisions in the global activity map so that
    // pill references in lane conditions and branch conditions resolve.
    // Stage-level decisions have no lane, so laneVarName is omitted.
    for (const entry of entries) {
        if (entry.type === 'decision') {
            const varName = guidToVarName.get(entry.record.getId().getValue())!
            globalActivityMap.set(entry.record.getId().getValue(), { varName })
        }
    }

    const pillContext: PillTransformContext = { activityMap: globalActivityMap }

    // First pass: process decisions to build branchGuidMap
    const branchGuidMap = new Map<string, { activityVarName: string; fluentBranchId: string }>()

    for (const entry of entries) {
        if (entry.type !== 'decision') {
            continue
        }
        const rec = entry.record
        const varName = guidToVarName.get(rec.getId().getValue())!
        const result = activityRecordToShape(rec, database, guidToVarName, branchGuidMap, pillContext, timerRecords)
        if (result.success && result.branchMap) {
            for (const [branchGuid, fluentBranchId] of result.branchMap) {
                branchGuidMap.set(branchGuid, { activityVarName: varName, fluentBranchId })
            }
        }
    }

    // Second pass: process all entries in order, building shapes
    const statements: Shape[] = []
    const returnEntries: Record<string, Shape> = {}

    for (const [i, entry] of entries.entries()) {
        const rec = entry.record
        const varName = varNames[i]!

        if (entry.type === 'decision') {
            const result = activityRecordToShape(rec, database, guidToVarName, branchGuidMap, pillContext, timerRecords)
            if (result.success) {
                statements.push(
                    new VariableStatementShape({ source, variableName: varName, initializer: result.value })
                )
                returnEntries[varName] = new IdentifierShape({ source, name: varName })
            }
        } else {
            const result = laneRecordToShape(
                rec,
                database,
                guidToVarName,
                timerRecords,
                globalActivityMap,
                branchGuidMap
            )
            if (result.success) {
                statements.push(
                    new VariableStatementShape({ source, variableName: varName, initializer: result.value })
                )
                returnEntries[varName] = new IdentifierShape({ source, name: varName })
            }
        }
    }

    // Empty collection: concise form `() => ({})`
    if (statements.length === 0) {
        return new ArrowFunctionShape({
            source,
            parameters: [],
            returnValue: new ObjectShape({ source, properties: {} }),
            implicitReturn: true,
        })
    }

    return new ArrowFunctionShape({
        source,
        parameters: [],
        statements,
        returnValue: new ObjectShape({ source, properties: returnEntries }),
    })
}

export const PlaybookDefinitionPlugin = Plugin.create({
    name: 'PlaybookDefinitionPlugin',
    records: {
        [PLAYBOOK_PROCESS_TYPE_TABLE]: {
            coalesce: ['name'],
        },
        [TIMER_ATTRIBUTES_TABLE]: {
            coalesce: ['source', 'source_type'],
        },
        [PROCESS_INPUT_TABLE]: {
            coalesce: ['element', 'model'],
        },
        [PLAYBOOK_OUTPUT_TABLE]: {
            coalesce: ['element', 'model'],
        },
        [PLAYBOOK_TABLE]: {
            relationships: {
                [PLAYBOOK_PROCESS_TYPE_TABLE]: {
                    via: 'process_type',
                    inverse: true,
                },
                [PROCESS_INPUT_TABLE]: {
                    via: 'model',
                    descendant: true,
                    relationships: {
                        sys_documentation: {
                            via: { name: 'name', element: 'element' },
                            descendant: true,
                        },
                        sys_choice: {
                            via: { name: 'name', element: 'element' },
                            descendant: true,
                        },
                    },
                },
                [PLAYBOOK_OUTPUT_TABLE]: {
                    via: 'model',
                    descendant: true,
                    relationships: {
                        sys_documentation: {
                            via: { name: 'name', element: 'element' },
                            descendant: true,
                        },
                        sys_choice: {
                            via: { name: 'name', element: 'element' },
                            descendant: true,
                        },
                    },
                },
                [TRIGGER_INSTANCE_TABLE]: {
                    via: 'process_definition',
                    descendant: true,
                    relationships: {
                        [SYS_VARIABLE_VALUE_TABLE]: {
                            via: 'document_key',
                            descendant: true,
                        },
                        [SYS_ELEMENT_MAPPING_TABLE]: {
                            via: 'id',
                            descendant: true,
                        },
                    },
                },
                [LANE_TABLE]: {
                    via: 'process_definition',
                    descendant: true,
                    relationships: {
                        [ACTIVITY_TABLE]: {
                            via: 'lane',
                            descendant: true,
                            relationships: {
                                [SYS_VARIABLE_VALUE_TABLE]: {
                                    via: 'document_key',
                                    descendant: true,
                                },
                                sys_element_mapping: {
                                    via: 'id',
                                    descendant: true,
                                },
                            },
                        },
                    },
                },
                [TIMER_ATTRIBUTES_TABLE]: {
                    via: 'process_definition',
                    descendant: true,
                },
                [ACTIVITY_TABLE]: {
                    via: 'process_definition',
                    descendant: true,
                },
                [VARIANT_TABLE]: {
                    via: 'process_definition',
                    descendant: true,
                },
            },
            async toShape(record, { descendants, database, logger }) {
                // Check for unsupported features (including schema_version) - fallback to RecordPlugin if found
                if (isPlaybookUnsupported(record, database, logger, descendants)) {
                    return { success: false }
                }

                const configShape = buildPlaybookConfigToShape(record, database)

                // Check for parent_record process input to extract parentTable.
                // Only inject parentTable into the generated Fluent when we have a
                // real, non-empty reference value — injecting '' would trigger the
                // diagnostics plugin's empty-parentTable check on round-trip.
                const processInputRecords = descendants.query(PROCESS_INPUT_TABLE)
                const parentRecordInput = processInputRecords.find(
                    (r) => r.get('element')?.ifString()?.getValue() === PARENT_RECORD.ELEMENT
                )
                const parentTableRef = parentRecordInput?.get('reference')?.ifString()?.getValue()
                const finalConfigShape = parentTableRef
                    ? configShape.merge({ parentTable: parentTableRef })
                    : configShape

                // Query timer attribute descendants for distribution to lanes and activities
                const timerRecords = descendants.query(TIMER_ATTRIBUTES_TABLE)

                // Shared mutable map populated incrementally by each lane's
                // buildActivityCollectionToShape, using the canonical topo-sort
                // order so variable names are guaranteed to align.
                const globalActivityMap = new Map<string, ActivityIdentifier>()

                const configExtensions: Record<string, Shape> = {}

                // Build inputs shape from process inputs, excluding parent_record (handled by parentTable)
                const nonParentInputRecords = processInputRecords.filter(
                    (r) => r.get('element')?.ifString()?.getValue() !== PARENT_RECORD.ELEMENT
                )
                const inputs = buildPlaybookVariableShapes(nonParentInputRecords, descendants)
                if (inputs && Object.keys(inputs).length > 0) {
                    configExtensions['inputs'] = new ObjectShape({
                        source: record,
                        properties: inputs,
                    })
                }

                // Build outputs shape from process output descendants
                const outputs = buildPlaybookVariableShapes(descendants.query(PLAYBOOK_OUTPUT_TABLE), descendants)
                if (outputs && Object.keys(outputs).length > 0) {
                    configExtensions['outputs'] = new ObjectShape({
                        source: record,
                        properties: outputs,
                    })
                }

                // Build triggers shape
                const triggerRecords = descendants.query(TRIGGER_INSTANCE_TABLE)
                const triggersArray = buildTriggersArrayToShape({
                    source: record,
                    triggerRecords,
                    descendants,
                })
                // Merge inputs/outputs into the config shape.
                const finalConfigWithIO = finalConfigShape.merge(configExtensions)

                // Always emit the triggers object, even when there are no triggers, so the
                // emitted signature is consistent. Empty triggers => `{ triggers: [] }`.
                const triggersCallbackShape: Shape = new ObjectShape({
                    source: record,
                    properties: {
                        triggers:
                            triggersArray ??
                            new ArrayShape({
                                source: record,
                                elements: [],
                            }),
                    },
                })
                // Query lane descendants
                const laneRecords = descendants.query(LANE_TABLE)

                // Query stage-level decision activities (empty lane field = stage-level)
                const allActivities = descendants.query(ACTIVITY_TABLE)
                const stageLevelDecisions = allActivities.filter((rec) => {
                    const laneVal = rec.get('lane')?.ifString()?.getValue() ?? ''
                    return laneVal === ''
                })

                // If no stage-level decisions, keep original behavior (lanes only)
                if (stageLevelDecisions.length === 0) {
                    const lanesArrow = buildChildCollectionToShape({
                        source: record,
                        childRecords: laneRecords,
                        database,
                        varPrefix: 'lane',
                        recordToShape: (rec, db, guidMap) =>
                            laneRecordToShape(rec, db, guidMap, timerRecords, globalActivityMap),
                        getDependencyGuids: (rec) => {
                            const raw = rec.get('starts_after_lanes')?.ifString()?.getValue() ?? ''
                            return raw
                                .split(',')
                                .map((g) => g.trim())
                                .filter(Boolean)
                        },
                    })

                    const bodyShape = new ObjectShape({
                        source: record,
                        properties: { lanes: lanesArrow },
                    })

                    return {
                        success: true,
                        value: new CallExpressionShape({
                            source: record,
                            callee: PLAYBOOK_API_NAME,
                            args: [finalConfigWithIO, triggersCallbackShape, bodyShape],
                        }),
                    }
                }

                // Stage-level decisions exist: build interleaved body
                const lanesArrow = buildInterleavedBodyToShape(
                    record,
                    laneRecords,
                    stageLevelDecisions,
                    database,
                    timerRecords,
                    globalActivityMap
                )

                const bodyShape = new ObjectShape({
                    source: record,
                    properties: { lanes: lanesArrow },
                })

                return {
                    success: true,
                    value: new CallExpressionShape({
                        source: record,
                        callee: PLAYBOOK_API_NAME,
                        args: [finalConfigWithIO, triggersCallbackShape, bodyShape],
                    }),
                }
            },
        },
    },
    shapes: [
        {
            shape: CallExpressionShape,
            fileTypes: ['fluent'],
            async toRecord(callExpression, { factory, transform, diagnostics, compiler, project, logger }) {
                if (callExpression.getCallee() !== PLAYBOOK_API_NAME) {
                    return { success: false }
                }

                const config = callExpression.getArgument(0).asObject()

                // Remind the user that deploying a playbook does not auto-activate changes at
                // runtime — surfaced in both the Fluent CLI and the Builder agent output.
                const playbookLabel = config.get('label')?.ifString()?.getValue() ?? ''
                logger.info(
                    `Playbook "${playbookLabel}" built. For any new changes to take effect at runtime, ` +
                        `open Playbook Designer and activate the playbook again.`
                )

                // Compute name value: use explicit name if provided, otherwise slugify from label
                const nameShape = config.get('name')
                const nameProvided = nameShape?.isDefined() && !nameShape?.isUndefined()
                let computedName: string | undefined
                if (!nameProvided) {
                    const labelVal = config.get('label')?.ifString()?.getValue()
                    computedName = labelVal ? slugifyString(labelVal) : ''
                }

                const processTypeShape = config.get('processType')
                const processTypeName = processTypeShape.ifString()?.getValue()?.trim()
                const mappedProcessTypeSysId =
                    processTypeName && processTypeName !== '' ? getPlaybookProcessTypeSysId(processTypeName) : undefined
                if (processTypeName && processTypeName !== '' && !mappedProcessTypeSysId) {
                    diagnostics.error(
                        processTypeShape,
                        `Unsupported processType "${processTypeName}". We do not have that process type defined in the SDK mapping.`
                    )
                    return { success: false }
                }
                const resolvedProcessType =
                    processTypeName && processTypeName !== ''
                        ? mappedProcessTypeSysId!
                        : PLAYBOOK_DEFAULT_PROCESS_TYPE_DB

                const playbookRecord = await factory.createRecord({
                    source: callExpression,
                    table: PLAYBOOK_TABLE,
                    explicitId: config.get('$id'),
                    properties: buildPlaybookRecordProperties(
                        config,
                        computedName,
                        !!nameProvided,
                        resolvedProcessType
                    ),
                })

                // Create sys_pd_process_input for parent_record if parentTable is set
                const parentTableVal = config.get('parentTable')?.ifString()?.getValue()
                let parentRecordInputRecord: Awaited<ReturnType<typeof factory.createRecord>> | undefined
                if (parentTableVal) {
                    parentRecordInputRecord = await factory.createRecord({
                        source: callExpression,
                        table: PROCESS_INPUT_TABLE,
                        properties: {
                            element: PARENT_RECORD.ELEMENT,
                            label: PARENT_RECORD.LABEL,
                            internal_type: PARENT_RECORD.INTERNAL_TYPE,
                            reference: parentTableVal,
                            order: PARENT_RECORD.ORDER,
                            mandatory: false,
                            active: true,
                            max_length: PARENT_RECORD.MAX_LENGTH,
                            model: playbookRecord,
                            model_id: playbookRecord,
                            model_table: '',
                            name: `var__m_${PROCESS_INPUT_TABLE}_${playbookRecord.getId().getValue()}`,
                            attributes: PARENT_RECORD.ATTRIBUTES,
                        },
                    })
                }

                // inputs/outputs live on the 1st-arg config (next to parentTable);
                const triggersCallback = callExpression.getArgument(1)
                const relatedRecords: SdkRecord[] = []

                // inputs/outputs read from the config object (arg 0)
                {
                    const inputsConfig = config.get('inputs')?.ifObject()?.asObject()
                    if (inputsConfig) {
                        const RESERVED_INPUT_NAMES = [PARENT_RECORD.ELEMENT, PARENT_RECORD_MAPPER_KEY]
                        const parentRecordKey = inputsConfig.keys().find((k) => RESERVED_INPUT_NAMES.includes(k))
                        if (parentRecordKey) {
                            diagnostics.error(
                                inputsConfig.get(parentRecordKey),
                                `"${parentRecordKey}" is a reserved input name. Use parentRecord in the input mapper to set the parent record instead.`
                            )
                        } else if (!parentTableVal) {
                            diagnostics.error(
                                inputsConfig,
                                'Defining inputs requires a parentTable in the playbook config.'
                            )
                        } else {
                            const inputRecords = await buildPlaybookVariableRecords({
                                variablesConfig: inputsConfig,
                                parentRecord: playbookRecord,
                                factory,
                                diagnostics,
                                parentTable: PLAYBOOK_TABLE,
                                varTable: PROCESS_INPUT_TABLE,
                            })
                            // column_label, use_reference_qualifier, and virtual_type are platform-managed
                            // constants required by the PAD designer — not exposed in the Fluent API and
                            // intentionally absent from toShape.
                            const patchedInputs = inputRecords.map((r) => {
                                if (r.getTable() !== PROCESS_INPUT_TABLE) {
                                    return r
                                }
                                return r.merge({
                                    column_label: r.get('label').getValue(),
                                    attributes: PLAYBOOK_INPUT_ATTRIBUTES,
                                    model_table: '',
                                    use_reference_qualifier: 'simple',
                                    virtual_type: 'script',
                                })
                            })
                            relatedRecords.push(...patchedInputs)
                        }
                    }

                    const outputsConfig = config.get('outputs')?.ifObject()?.asObject()
                    if (outputsConfig) {
                        if (!parentTableVal) {
                            diagnostics.error(
                                outputsConfig,
                                'Defining outputs requires a parentTable in the playbook config.'
                            )
                        } else {
                            const outputRecords = await buildPlaybookVariableRecords({
                                variablesConfig: outputsConfig,
                                parentRecord: playbookRecord,
                                factory,
                                diagnostics,
                                parentTable: PLAYBOOK_TABLE,
                                varTable: PLAYBOOK_OUTPUT_TABLE,
                            })
                            // column_label, use_reference_qualifier, and virtual_type are platform-managed
                            // constants required by the PAD designer — not exposed in the Fluent API and
                            // intentionally absent from toShape.
                            const patchedOutputs = outputRecords.map((r) => {
                                if (r.getTable() !== PLAYBOOK_OUTPUT_TABLE) {
                                    return r
                                }
                                return r.merge({
                                    column_label: r.get('label').getValue(),
                                    model_table: '',
                                    use_reference_qualifier: 'simple',
                                    virtual_type: 'script',
                                })
                            })
                            relatedRecords.push(...patchedOutputs)
                        }
                    }
                }

                const playbookInputOnlyRecords = relatedRecords.filter((r) => r.getTable() === PROCESS_INPUT_TABLE)

                const triggerRecords = triggersCallback
                    ? await processTriggersArrayToRecord({
                          triggersObject: triggersCallback,
                          parentRecord: playbookRecord,
                          parentTable: parentTableVal,
                          parentRecordInputRecord,
                          playbookInputRecords: playbookInputOnlyRecords,
                          compiler,
                          rootDir: project.getRootDir(),
                          diagnostics,
                          transform,
                          factory,
                      })
                    : []

                // Extract body (argument 2) and process children
                const bodyArg = callExpression.getArgument(2)
                const lanesArrow = bodyArg?.asObject().get('lanes')
                const lanesArrowFn = lanesArrow?.is(ArrowFunctionShape) ? lanesArrow.as(ArrowFunctionShape) : undefined
                const playbookBodyParamsName = lanesArrowFn?.getParameters()[0]?.getName()
                const playbookBodyParamsNames: string[] = []
                if (playbookBodyParamsName) {
                    playbookBodyParamsNames.push(playbookBodyParamsName)
                }

                // Per-callee dispatch for stage-level activity entries
                const activityTypeConfig: ChildCollectionTypeConfig = {
                    configArgIndex: 1,
                    createShape: (
                        source: Shape,
                        cfg: ObjectShape,
                        context?: { nowIdKeyToGuid?: Map<string, string> }
                    ) => {
                        const callExpr = source.as(CallExpressionShape)
                        const arg2 = callExpr.getArgument(2)?.ifObject()
                        const arg3 = callExpr.getArgument(3)?.ifObject()
                        const argsObj = callExpr.getArgument(1)?.ifObject()
                        return new ActivityShape({
                            source,
                            activityDefinition: callExpr.getArgument(0, false),
                            config: cfg,
                            inputs: arg2 ?? argsObj?.get('inputs')?.ifObject(),
                            experienceProperties: arg3 ?? argsObj?.get('experienceProperties')?.ifObject(),
                            ...(context?.nowIdKeyToGuid !== undefined
                                ? { nowIdKeyToGuid: context.nowIdKeyToGuid }
                                : {}),
                            playbookBodyParamsNames,
                        })
                    },
                    childPlugin: ActivityPlugin,
                }
                const laneTypeConfig: ChildCollectionTypeConfig = {
                    createShape: (source, cfg, context) => {
                        const laneArg = source.is(CallExpressionShape)
                            ? source.as(CallExpressionShape).getArgument(0)?.ifObject()
                            : undefined
                        const activitiesShape = laneArg?.get('activities')
                        const activities = activitiesShape?.is(ArrowFunctionShape)
                            ? activitiesShape.as(ArrowFunctionShape)
                            : undefined
                        return new LaneShape({
                            source,
                            config: cfg,
                            activities,
                            ...(context?.nowIdKeyToGuid !== undefined
                                ? { nowIdKeyToGuid: context.nowIdKeyToGuid }
                                : {}),
                            playbookBodyParamsName,
                        })
                    },
                    childPlugin: LanePlugin,
                }
                const childTypeMap = new Map<string, ChildCollectionTypeConfig>([
                    ['wfa.playbook.lane', laneTypeConfig],
                    ['Lane', laneTypeConfig],
                    [ACTIVITY_API_NAME, activityTypeConfig],
                    ['wfa.playbook.activity', activityTypeConfig],
                ])

                // Single pass: process lanes and stage-level activities in declaration order.
                // Sequential processing ensures each entry sees GUIDs and branch maps
                // from everything declared before it.
                const allRecords = lanesArrow?.is(ArrowFunctionShape)
                    ? await processChildCollectionToRecord({
                          collectionArrow: lanesArrow.as(ArrowFunctionShape),
                          parentRecord: playbookRecord,
                          parentFkField: 'processDefinition',
                          calleeNames: ['wfa.playbook.lane', 'Lane', ACTIVITY_API_NAME, 'wfa.playbook.activity'],
                          childTypeMap,
                          resolveReferences: resolveStartsAfterLanes,
                          resolvedRefField: 'startsAfterLanes',
                          resolveBranchReferences: resolveStartsAfterBranches,
                          resolvedBranchRefField: 'startsAfterBranches',
                          resolvedBranchActivityField: 'startsAfterActivities',
                          extractBranchIdMap: extractDecisionBranchIdMap,
                          computePerChildMergeProps: (allConfigs, index, allCallees) => {
                              // De-dupe against the full pool of names. Explicit names are kept;
                              // generated names (slugified labels) yield to anything already taken,
                              // so an auto-generated name never collides with an explicit one.
                              // Children here are a mix of lanes and stage-level activities: strip
                              // HTML only for activities (the platform BR strips HTML from
                              // sys_pd_activity.label), while lane labels slug as-is.
                              const activityCallees = new Set([ACTIVITY_API_NAME, 'wfa.playbook.activity'])
                              const entries = allConfigs.map((c, i) => {
                                  const explicit = c.get('name')?.ifString()?.getValue()
                                  if (explicit) {
                                      return { name: explicit, isExplicit: true }
                                  }
                                  const label = c.get('label')?.ifString()?.getValue() ?? ''
                                  const slug = slugifyString(
                                      activityCallees.has(allCallees[i] ?? '') ? stripHtmlTags(label) : label
                                  )
                                  return { name: slug, isExplicit: false }
                              })
                              const uniqueName = deduplicateNames(entries)[index] ?? ''
                              const entry = entries[index]
                              if (entry?.isExplicit && entry.name !== uniqueName) {
                                  diagnostics.warn(
                                      allConfigs[index] ?? callExpression,
                                      `The name "${entry.name}" is already in use by another activity; it has been renamed to "${uniqueName}" to keep names unique.`
                                  )
                              }
                              return { uniqueName }
                          },
                          validateChildren: (children, variables) => {
                              validateOrderUniqueness(children, diagnostics)
                              const redundantEdges = detectRedundantEdges(children, variables)
                              for (const {
                                  nodeId,
                                  redundantDepId,
                                  throughId,
                                  shape,
                                  nodeTypeLabel,
                              } of redundantEdges) {
                                  diagnostics.warn(
                                      shape,
                                      `${nodeTypeLabel} "${nodeId}" has a redundant start rule dependency on "${redundantDepId}" because it is already reachable through "${throughId}".`
                                  )
                              }
                          },
                          transform,
                          diagnostics,
                          errorMessage: 'PlaybookDefinition lanes function must return an object literal',
                          errorSource: callExpression,
                      })
                    : []

                const allChildRecords = [
                    ...allRecords,
                    ...triggerRecords,
                    ...relatedRecords,
                    ...(parentRecordInputRecord ? [parentRecordInputRecord] : []),
                ]

                return {
                    success: true,
                    value: playbookRecord.with(...allChildRecords),
                }
            },
        },
    ],
    docs: [],
})
